/**
 * The Cloud picker's decisions, kept apart from its page so they can be tested.
 *
 * `/picker?for=relay` opens in a popup from Relay's web client. The person
 * uploads or chooses files, each gets a share link, and the page posts the
 * links back to the window that opened it. Three things here decide what that
 * is allowed to mean:
 *
 *  · **Who receives the result.** A fixed map keyed on `for` and on this page's
 *    own host. Never a parameter: a picker that posts wherever the address says
 *    is a page that hands share links to any site able to open a window.
 *  · **When the page refuses.** An unknown client, an unknown host, no opener
 *    to post to, or an opener that is not that client. The last is checked
 *    before the vault unlocks, not only before the post: the picker keeps its
 *    opener, so it shares that page's browsing-context group.
 *  · **What leaves.** `pickerResult` copies the seven link fields by name, so a
 *    key held beside them cannot travel by accident. The link secret does leave
 *    — it is the link — and that is the same thing Relay's Qt client posts.
 */

import { clientOrigin } from './clients';

export const PICKER_MESSAGE = 'mauvely-cloud-picker';

/** Files one confirm may hand back. Relay checks the same number. */
export const MAX_PICKER_FILES = 20;

/** Where uploads land: the folder Relay's Qt client uses, `kAppFolder` in
 *  Programs/relay/src/cloud/cloudservice.cpp. */
export const RELAY_FOLDER = 'Relay';

export type PickerGate =
  | { ok: true; target: string }
  | { ok: false; reason: 'unknown-client' | 'no-opener' | 'wrong-opener' };

/**
 * `referrer` is `document.referrer`. Relay sends
 * `strict-origin-when-cross-origin`, so a popup it opens sees Relay's origin
 * there, and no other page can put it there.
 */
export function pickerGate(
  search: string,
  hostname: string,
  hasOpener: boolean,
  referrer: string
): PickerGate {
  const client = new URLSearchParams(search).get('for') ?? '';
  // Hosts `clientOrigin` does not list get no picker.
  const target = clientOrigin(client, hostname);
  if (!target) return { ok: false, reason: 'unknown-client' };
  if (!hasOpener) return { ok: false, reason: 'no-opener' };
  if (originOf(referrer) !== target) return { ok: false, reason: 'wrong-opener' };
  return { ok: true, target };
}

function originOf(url: string): string | null {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

/** The gate for this window. Read once: nothing later can change the answer. */
export function pickerGateHere(): PickerGate {
  return pickerGate(
    window.location.search,
    window.location.hostname,
    Boolean(window.opener),
    document.referrer
  );
}

/** `/picker`, matched the way the Worker's COOP header matches it. */
export function isPickerPath(pathname: string): boolean {
  return pathname.replace(/\/+$/, '') === '/picker';
}

/** One shared file, as the page knows it once its link exists. */
export interface PickedFile {
  nodeId: string;
  revisionId: string;
  linkId: string;
  /** The link secret, the URL's fragment. */
  secret: string;
  name: string;
  mime: string;
  bytes: number;
}

/** Relay's `WireFile`, in `web/src/lib/relay/payload.ts` there. */
export interface WireFile {
  id: string;
  s: string;
  l: string;
  r: string;
  x: string;
  m: string;
  b: number;
}

export interface PickerResult {
  type: typeof PICKER_MESSAGE;
  v: 1;
  files: WireFile[];
}

// Cloud's ids are `prefix_` and hex; the secret is 32 bytes as base64url.
const ID = /^[A-Za-z0-9_-]{1,64}$/;
const SECRET = /^[A-Za-z0-9_-]{43}$/;
const text = (s: unknown, max: number, min = 0) =>
  typeof s === 'string' && s.length >= min && s.length <= max;

/** A name Relay's wire takes. Checked before uploading, as well as here. */
export const nameFits = (name: string) => text(name, 255, 1);

function wireFile(f: PickedFile): WireFile {
  const ok =
    ID.test(f.nodeId) &&
    ID.test(f.revisionId) &&
    ID.test(f.linkId) &&
    SECRET.test(f.secret) &&
    nameFits(f.name) &&
    text(f.mime, 255) &&
    Number.isSafeInteger(f.bytes) &&
    f.bytes >= 0;
  if (!ok) throw new Error('A file in the picker result is malformed.');
  return { id: f.nodeId, s: f.secret, l: f.linkId, r: f.revisionId, x: f.name, m: f.mime, b: f.bytes };
}

export function pickerResult(files: PickedFile[]): PickerResult {
  if (files.length < 1 || files.length > MAX_PICKER_FILES) {
    throw new Error('The picker result holds the wrong number of files.');
  }
  return { type: PICKER_MESSAGE, v: 1, files: files.map(wireFile) };
}

/** `Brief.pdf` → `Brief (2).pdf`, for a name already taken in the folder. */
export function numberedName(name: string, n: number): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? `${name.slice(0, dot)} (${n})${name.slice(dot)}` : `${name} (${n})`;
}
